import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { isAxiosError } from 'axios';
import { toast } from '@/components/ui/sonner';
import { pulseService } from '@/services/pulse.service';
import { invalidateQueries } from '@/lib/query-client';
import { backgroundJobsAtom, removeBackgroundJobAtom } from '@/atoms/background-jobs';
import type { BackgroundJob } from '@/atoms/background-jobs';
import { POLL_INTERVAL_MS } from '@/modules/pulse/constants';

function getLoadingMessage(job: BackgroundJob): string {
  return `Drafting protocols for "${job.label}"…`;
}

export function BackgroundJobPoller() {
  const [jobs] = useAtom(backgroundJobsAtom);
  const [, removeJob] = useAtom(removeBackgroundJobAtom);
  const pollingRef = useRef(false);

  // Show a loading toast for each active background job.
  useEffect(() => {
    for (const [jobId, job] of jobs.entries()) {
      toast.loading(getLoadingMessage(job), { id: jobId });
    }
  }, [jobs]);

  useEffect(() => {
    if (jobs.size === 0) return;

    const interval = setInterval(async () => {
      if (pollingRef.current) return;
      pollingRef.current = true;

      const entries = Array.from(jobs.entries());

      for (const [jobId, job] of entries) {
        try {
          const result = await pulseService.getJobStatus(jobId);

          if (result.status === 'completed') {
            removeJob(jobId);
            handleCompleted(job, result.result);
          } else if (result.status === 'failed') {
            removeJob(jobId);
            handleFailed(job, result.error);
          }
        } catch (err) {
          // If the backend returned a 500 with a terminal job status, handle it
          if (isAxiosError(err) && err.response) {
            const jobData = err.response.data?.data;
            if (jobData?.status === 'failed') {
              removeJob(jobId);
              handleFailed(job, jobData.error ?? null);
            } else if (jobData?.status === 'completed') {
              removeJob(jobId);
              handleCompleted(job, jobData.result ?? null);
            }
            // else: unexpected error response — retry on next tick
          }
          // Network error — retry on next tick
        }
      }

      pollingRef.current = false;
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [jobs, removeJob]);

  return null;
}

function handleCompleted(job: BackgroundJob, result: Record<string, unknown> | null) {
  invalidateQueries(['pulse']);
  invalidateQueries(['sops']);
  // A run can finish with no error but still create nothing new — every draft turned out
  // to be a duplicate/subtype of an already-existing SOP. Don't claim success in that case.
  const sopsCreated = typeof result?.['sopsCreated'] === 'number' ? (result['sopsCreated'] as number) : null;
  if (sopsCreated === 0) {
    toast.info(`No new draft was needed for "${job.label}" — it matched an existing protocol`, { id: job.jobId });
  } else {
    toast.success(`Protocol drafts for "${job.label}" are ready to review`, { id: job.jobId });
  }
}

function handleFailed(job: BackgroundJob, error: string | null) {
  invalidateQueries(['pulse']);
  toast.error(error ?? `Drafting protocols failed for "${job.label}"`, { id: job.jobId });
}
